<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('warehouse.shipping_bill')"
    icon="mdi-arrow-right"
    color="blue"
    :save-text="$t('warehouse.ship')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="save"
    :hide-footer="editedItem?.id"
  >
    <template v-slot:app-bar-actions>
      <print-button @click="print"></print-button>
    </template>

    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" lg="4" xl="2">
          <choose-warehouse-field
            v-model="editedItem.warehouse"
            return-object
            hide-details
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            hide-details
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <date-picker
            v-model="editedItem.document_date"
            single-date
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <autocomplete
            v-model="editedItem.dispatched_by"
            :items="users"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.delivered_by')"
            required
            :rules="[rules.required]"
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <autocomplete
            v-model="editedItem.received_by"
            :items="users"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.received_by')"
            required
            :rules="[rules.required]"
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="4" xl="2">
          <text-area
            :label="$t('note')"
            v-model="editedItem.notes"
            rows="1"
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-purchase-order-table
      v-if="selectedWarehouse"
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse"
      :headers="headers"
      waybill
      @removeGrocery="removeGrocery"
      :disabled="editedItem?.id"
    />
    <div
      class="text-center grey--text text--darken-1 caption mt-2"
      v-if="selectedWarehouse"
    >
      <v-icon small color="grey darken-1">mdi-keyboard-return</v-icon>
      {{ $t("warehouse.quick_add_hint") }}
    </div>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasPrinting from "@/mixins/hasPrinting";
import hasSelectedWarehouse from "@/mixins/warehouse/hasSelectedWarehouse";
import ChooseWarehouseField from "@/components/warehouse/field/ChooseWarehouseField.vue";
import WarehousePurchaseOrderTable from "@/components/warehouse/requirement/purchase/WarehousePurchaseOrderTable.vue";
import { waybillDocumentGrocery } from "@/models/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  name: "WarehouseDialogOrder",
  mixins: [hasModel, hasValidation, hasPrinting, hasSnackbar, hasSelectedWarehouse],
  components: {
    ChooseWarehouseField,
    WarehousePurchaseOrderTable,
    FullscreenDialog,
  },
  props: {
    editedItem: {
      type: Object,
      default: waybillDocumentGrocery,
    },
  },
  data() {
    return {
      search: "",
      valid: false,
      loadingUsers: false,
      loading: false,
    };
  },
  methods: {
    removeGrocery(grocery) {
      this.editedItem.groceries = this.editedItem.groceries.filter(
        (item) => grocery.id !== item.id
      );
    },
    print() {
      const headers = this.headers.filter((header) => header.value !== "actions");
      const items = this.editedItem.groceries.map((item, index) => ({
        ...item,
        num: index + 1,
      }));

      const dispatchedBy = this.users.find(
        (user) => user.id === this.editedItem.dispatched_by
      );
      const receivedBy = this.users.find(
        (user) => user.id === this.editedItem.received_by
      );
      this.printDocument({
        tables: [{ headers, items }],
        title: this.$t("delivery_note_no") + " " + this.editedItem.number,
        beforeTitleHtml: `
          <h2 style="margin-bottom: 0px">${this.$t("stock")}: 
            ${this.toLatin(this.editedItem.warehouse?.name || "")}
          </h2>
            <div style="font-size: 16px;margin-top: 20px;">
              ${this.$t("date")}: ${this.localeDate(this.editedItem.document_date)}
            </div>
          `,
        signature: {
          right: {
            title: this.$t("warehouse.received_by"),
            name: this.toLatin([receivedBy?.name, receivedBy?.lastname].join(" ") || ""),
          },
          left: {
            title: this.$t("warehouse.delivered_by"),
            name: this.toLatin(
              [dispatchedBy?.name, dispatchedBy?.lastname].join(" ") || ""
            ),
          },
        },
        hideDatetime: true,
      });
    },
    async save() {
      const params = {
        ...this.editedItem,
        transaction_type: "exit",
        groceries: this.editedItem.groceries.map((grocery) => ({
          ...grocery,
          warehouse_id: this.editedItem.warehouse.id,
          grocery_id: grocery.grocery_id,
          transaction_type: "exit",
        })),
      };
      const request = warehouseService.addDocumentTransaction(params);
      try {
        this.loading = true;
        await this.handleRequest(request, {
          successMessage: this.$t("warehouse.shipping_created_successfully"),
          errorMessagePrefix: this.$t("error"),
        });
        this.model = false; // Close dialog on success
        this.$emit("success"); // Emit success event
      } catch (error) {
        // Error is already handled by handleRequest,
        // but you can add additional component-specific error handling here if needed.
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false; // Reset loading state
      }
    },
    async fetchUsers(refetch = false) {
      try {
        this.loadingUsers = true;
        await this.$store.dispatch("restaurantStore/dispatchUsers", { refetch });
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.loadingUsers = false;
      }
    },
  },
  computed: {
    users() {
      return this.$store.state.restaurantStore.users.map((user) => ({
        ...user,
        fullName: [user.name, user.lastname].map((name) => this.toLatin(name)).join(" "),
      }));
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", width: "100px" },
        {
          text: this.$t("code"),
          align: "start",
          value: "code",
          width: "150px",
        },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("quantity"), value: "quantity" },
        { text: this.$t("warehouse.warehouse_status"), value: "warehouse_stock" },
        { text: "", value: "actions", width: "100px" },
      ];
    },
  },
  watch: {
    model: {
      handler() {
        if (!this.users.length) {
          this.fetchUsers();
        }
      },
      immediate: true,
    },
  },
};
</script>
